import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpEvent, HttpEventType, HttpRequest, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, EMPTY, from, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, filter, map, mergeMap, retry, tap, toArray} from 'rxjs/operators';
import {
  FileMetadataDTO,
  FileUploadStringTransfer,
  MultipartPartUrl,
  ProjectFile,
  ProjectFileControllerService
} from '../../api';
import {BackgroundProgress, mergeProgress} from '../background-progress.model';
import {ClassificationService} from '../classification/classification.service';

export type UploadJobState = 'queued' | 'uploading' | 'processing' | 'done' | 'error';

/** Ab dieser Dateigröße wird per S3 Multipart Upload hochgeladen (Single-PUT ist bei S3 auf 5 GiB begrenzt). */
const MULTIPART_THRESHOLD_BYTES = 100 * 1024 * 1024; // 100 MiB
/** Wie viele Parts gleichzeitig zu S3 hochgeladen werden. */
const MULTIPART_PART_CONCURRENCY = 3;
/**
 * Budget gleichzeitiger Uploads, gemessen in offenen Verbindungen zu S3: eine kleine Datei belegt
 * eine, ein Multipart-Upload MULTIPART_PART_CONCURRENCY. 6 entspricht dem, was Browser pro Host
 * parallel zulassen \- mehr würde nur in der Browser-Queue warten.
 */
const MAX_PARALLEL_REQUESTS = 6;
/**
 * Wie viele Dateien pro Presign-Request gebündelt werden.
 *
 * Vorher holte sich jede Datei ihre presigned URL mit einem eigenen POST /files/upload. Das war nicht
 * nur ein Request pro Datei, sondern jeder davon leistete im Backend Arbeit in der Größe des
 * Zielordners \- bei N Dateien in denselben Ordner also O(N²). Der Sammel-Endpunkt erledigt das in
 * einem Aufruf.
 *
 * Warum trotzdem gestückelt und nicht alles in EINEM Request: der Chunk begrenzt die Größe des
 * Request-Bodys und die Zahl der Zeilen, die ein einzelner Request schreibt, ohne die Ersparnis
 * aufzugeben (100 Dateien = 1 Request statt 100). Die Chunks laufen nacheinander, siehe presignQueue.
 */
const PRESIGN_BATCH_SIZE = 100;
/**
 * Wie viele hochgeladene Dateien je Request zur Klassifizierung eingereicht werden.
 *
 * Der Kompromiss: gross genug, dass aus ~380 Einzelrequests ~8 werden, klein genug, dass eine Datei
 * nicht bis zum Ende des ganzen Uploads auf Status UPLOADING haengt.
 */
const CLASSIFICATION_CHUNK_SIZE = 50;

export interface UploadJob {
  clientId: string;
  /** Klammert alle Dateien einer Auswahl zusammen (ein Ordner-Upload erzeugt mehrere enqueueFiles-Aufrufe). */
  batchId: string;
  projectId: number;
  file: File;
  parentId?: string;
  createdAt: number;

  fileId?: string;
  projectFile?: ProjectFile;

  /**
   * Presigned PUT-URL für den Single-Upload. Wird für kleine Dateien vorab gebündelt geholt
   * (siehe PRESIGN_BATCH_SIZE); Multipart-Jobs holen ihre Part-URLs weiterhin selbst.
   */
  uploadUrl?: string;

  /** Nur bei Multipart-Uploads gesetzt \- benötigt für complete/abort. */
  uploadId?: string;

  progress: number; // 0..100
  state: UploadJobState;
  error?: unknown;
}

/**
 * Zentraler Upload-Service.
 * Idee: Uploads laufen weiter, auch wenn die Files-Component destroyed wird (Navigation).
 * Komponenten abonnieren nur noch den Stream (jobs$) und rendern Status/Progress.
 */
@Injectable({providedIn: 'root'})
export class UploadService {
  private readonly fileController = inject(ProjectFileControllerService);
  private readonly http = inject(HttpClient);
  private readonly classificationService = inject(ClassificationService);

  private readonly jobsSubject = new BehaviorSubject<UploadJob[]>([]);
  public readonly jobs$ = this.jobsSubject.asObservable();

  private readonly jobUpdatesSubject = new Subject<UploadJob>();
  public readonly jobUpdates$ = this.jobUpdatesSubject.asObservable();

  private readonly activeSubscriptions = new Map<string, Subscription>();
  /** Jobs, die abgebrochen wurden, bevor das Backend eine fileId geliefert hat (siehe cancelJob). */
  private readonly cancelRequestedClientIds = new Set<string>();
  /**
   * Jobs, deren Sammel-Presign noch unterwegs ist. Sie stehen noch NICHT in queuedClientIds und haben
   * noch keine fileId \- ein Abbruch in diesem Fenster muss deshalb genauso vorgemerkt werden wie
   * während eines laufenden Uploads (siehe cancelJob).
   */
  private readonly presignPendingClientIds = new Set<string>();
  /**
   * Hochgeladene Dateien je Stapel, die noch nicht zur Klassifizierung eingereicht wurden.
   *
   * Sie gehen in Bloecken raus (siehe CLASSIFICATION_CHUNK_SIZE) und nicht erst am Ende: eine Datei
   * steht serverseitig auf UPLOADING, bis ihre Klassifizierung startet. Ging der Stapel erst nach
   * dem letzten Upload raus, sah eine frueh fertige Datei die ganze Uploadzeit ueber aus, als lade
   * sie noch - bei 380 Dateien also minutenlang.
   */
  private readonly classificationPendingByBatchId = new Map<string, {fileId: string, name: string}[]>();

  /**
   * Dateien, deren Bytes durch sind und die dem Server noch als hochgeladen gemeldet werden muessen.
   *
   * Der Server erfaehrt es sonst nicht: die Bytes gehen direkt vom Browser zu S3, danach kommt kein
   * Aufruf mehr. Die Zeile blieb deshalb auf UPLOADING, bis ihr Klassifizierungs-Block rausging -
   * und weil UPLOADING damit "Bytes laufen noch" UND "Bytes durch, Verarbeitung wartet" bedeutete,
   * zeigte die Dateiansicht einen Upload-Ring bei 100 %. Seit es UPLOADED gibt, ist das eindeutig.
   */
  private readonly uploadedToReport: string[] = [];
  /**
   * Ob gerade eine Meldung unterwegs ist.
   *
   * Nachlaufende Buendelung statt fester Blockgroesse oder Timer: waehrend ein Request laeuft,
   * sammelt sich der Rest an und geht danach in EINEM Aufruf raus. Damit ist immer hoechstens eine
   * Meldung unterwegs, sie kommt so zeitnah wie die Leitung es zulaesst, und bei 380 Dateien sind es
   * ein paar Dutzend kleine Requests statt einer je Datei.
   */
  private uploadedReportInFlight = false;

  private readonly uploadedReportedSubject = new Subject<string[]>();

  /**
   * Datei-IDs, deren UPLOADED-Meldung der Server bestaetigt hat.
   *
   * Notwendig, weil es fuer den Statuswechsel keinen Push-Kanal gibt: SSE meldet nur den ABSCHLUSS
   * einer Klassifizierung. Ohne dieses Signal stand die Dateiliste weiter auf UPLOADING, und der
   * Ordner zaehlte als "hochgeladen" nur die Dateien, die schon fertig klassifiziert waren - bei
   * 381 Dateien also 4, obwohl laengst hunderte durch waren.
   *
   * Kein zusaetzlicher Request: dieser Tab hat die Meldung selbst geschickt und spiegelt hier nur,
   * was er dem Server gerade geschrieben hat.
   */
  public readonly uploadedReported$ = this.uploadedReportedSubject.asObservable();

  /**
   * Jobs, die schon eingesammelt wurden.
   *
   * Notwendig, weil der Abschluss-Check nach JEDER Datei laeuft: ohne den Merker landete dieselbe
   * Datei mehrfach in einem Block.
   */
  private readonly classificationCollectedClientIds = new Set<string>();
  /**
   * Noch nicht abgeschickte Presign-Chunks, plus ein Flag fuer den gerade laufenden.
   *
   * Die Chunks laufen bewusst STRENG NACHEINANDER. Parallel abgeschickt schreiben mehrere
   * uploadBatch-Requests gleichzeitig in {@code project_file} \- und weil ein Ordner-Upload
   * zusaetzlich einmal pro Zielordner enqueueFiles() aufruft, waren das schnell mehrere parallele
   * schreibende Requests. Ergebnis waren SQL-Server-Deadlocks (Fehler 1205), die den Request mit 500
   * beendeten. Serialisiert kostet es nichts Sichtbares: die Uploads des ersten Chunks laufen bereits,
   * waehrend der zweite Chunk presignt wird.
   */
  private readonly presignQueue: { jobs: UploadJob[]; projectId: number; parentId?: string }[] = [];
  private presignInFlight = false;

  /** Aktuell belegtes Verbindungsbudget (siehe MAX_PARALLEL_REQUESTS) je laufendem Job. */
  private readonly slotWeightByClientId = new Map<string, number>();
  private activeWeight = 0;
  private readonly queuedClientIds: string[] = [];

  public getJobsSnapshot(): UploadJob[] {
    return this.jobsSubject.value;
  }

  public getJobsForProject(projectId: number): UploadJob[] {
    return this.jobsSubject.value.filter(j => j.projectId === projectId);
  }

  /**
   * Bricht alle laufenden Uploads ab, deren Ziel in den genannten Ordnern liegt.
   *
   * Gegenstueck zum Loeschen eines Ordners, in den gerade hochgeladen wird. Frueher war das
   * verboten - serverseitig mit 409, in der Ansicht mit einer Meldung. Das war die falsche Antwort
   * auf die richtige Sorge: eine haengengebliebene UPLOADING-Zeile machte damit ihren ganzen
   * Ordnerbaum dauerhaft unloeschbar.
   *
   * Richtig ist: wer den Ordner loescht, will den Upload dorthin nicht fortsetzen. Also wird er
   * abgebrochen - ueber {@link cancelJob}, der auch den halb angelegten Datei-Record im Backend
   * wieder wegraeumt. Damit bleibt weder eine Karteileiche noch eine Fortschrittszahl stehen, die
   * zu keiner sichtbaren Zeile mehr gehoert.
   *
   * @param parentIds IDs der geloeschten Ordner samt Teilbaum.
   * @returns Anzahl der abgebrochenen Jobs.
   */
  /**
   * Ob DIESER Tab gerade Bytes fuer diese Datei uebertraegt.
   *
   * Bewusst tab-lokal und nicht "der Server sagt UPLOADING". Der Grund, aus dem Verschieben,
   * Kopieren und Umbenennen waehrend eines Uploads gesperrt sind, ist ein tab-lokaler: die
   * laufenden Jobs tragen die alte parentId, und eine Zeile ohne Server-ID faellt beim Mappen auf
   * IDs stillschweigend heraus. Beides betrifft nur die Jobs hier.
   *
   * Der Serverstatus taugt dafuer nicht: eine Zeile bleibt auf UPLOADING stehen, wenn der Tab
   * weggebrochen ist, der die Bytes hatte - und sperrte damit ihren Ordnerbaum dauerhaft.
   */
  public hasActiveJobForFileId(fileId: string): boolean {
    return this.jobsSubject.value.some(job =>
      job.fileId === fileId
      && (job.state === 'queued' || job.state === 'uploading' || job.state === 'processing')
      && job.error !== 'cancelled');
  }

  public cancelUploadsInto(projectId: number, parentIds: Set<string>): number {
    if (parentIds.size === 0) return 0;

    const clientIds = this.jobsSubject.value
      .filter(job => job.projectId === projectId)
      .filter(job => job.state === 'queued' || job.state === 'uploading' || job.state === 'processing')
      .filter(job => !!job.parentId && parentIds.has(job.parentId))
      .map(job => job.clientId);

    // Erst komplett aus der Warteschlange nehmen, damit dispatchQueue() waehrenddessen keinen
    // weiteren Job desselben Ordners nachzieht - dieselbe Reihenfolge wie in cancelBatch().
    for (const clientId of clientIds) {
      const idx = this.queuedClientIds.indexOf(clientId);
      if (idx !== -1) this.queuedClientIds.splice(idx, 1);
    }
    for (const clientId of clientIds) {
      this.cancelJob(clientId);
    }
    return clientIds.length;
  }

  /** Erzeugt eine batchId, mit der eine Dateiauswahl über mehrere enqueueFiles-Aufrufe hinweg zusammengehalten wird. */
  public createBatchId(): string {
    return `batch-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  public enqueueFiles(projectId: number, files: File[], parentId?: string, batchId: string = this.createBatchId()): UploadJob[] {
    const now = Date.now();
    const newJobs: UploadJob[] = files.map((file, idx) => ({
      clientId: `${projectId}-${now}-${idx}-${Math.random().toString(16).slice(2)}`,
      batchId,
      projectId,
      file,
      parentId,
      createdAt: now,
      progress: 0,
      state: 'queued'
    }));

    this.jobsSubject.next([...newJobs, ...this.jobsSubject.value]);

    // Multipart-Jobs holen ihre URLs selbst (multipart/initiate) und können sofort starten.
    const multipartJobs = newJobs.filter(job => job.file.size >= MULTIPART_THRESHOLD_BYTES);
    for (const job of multipartJobs) {
      this.queuedClientIds.push(job.clientId);
    }

    // Kleine Dateien: presigned URLs gebündelt holen. Sie wandern erst mit der Antwort in die
    // Warteschlange \- ohne URL könnte der Dispatcher sie nicht starten.
    const singleJobs = newJobs.filter(job => job.file.size < MULTIPART_THRESHOLD_BYTES);
    for (let i = 0; i < singleJobs.length; i += PRESIGN_BATCH_SIZE) {
      this.enqueuePresignChunk(singleJobs.slice(i, i + PRESIGN_BATCH_SIZE), projectId, parentId);
    }

    this.dispatchQueue();

    return newJobs;
  }

  public cancelJob(clientId: string): void {
    const job = this.jobsSubject.value.find(j => j.clientId === clientId);
    if (!job) return;

    // Der Datei-Record im Backend entsteht erst mit der Antwort von upload/initiate. Bricht der Nutzer
    // vorher ab, würde ein sofortiges unsubscribe die Antwort verwerfen \- die fileId wäre für immer
    // unbekannt und der Record bliebe als Karteileiche in UPLOADING stehen. Deshalb hier nur vormerken:
    // sobald die fileId eintrifft, räumt setJob() auf.
    if (!job.fileId && (this.activeSubscriptions.has(clientId) || this.presignPendingClientIds.has(clientId))) {
      this.cancelRequestedClientIds.add(clientId);
      this.markCancelled(clientId);
      return;
    }

    this.hardCancel(clientId);
    this.markCancelled(clientId);
  }

  /**
   * Reiht einen Schwung Dateien fuer das gebuendelte Presign ein. Abgeschickt wird immer nur ein
   * Chunk gleichzeitig (siehe presignQueue).
   */
  private enqueuePresignChunk(jobs: UploadJob[], projectId: number, parentId?: string): void {
    if (jobs.length === 0) return;

    // Sofort vormerken, nicht erst beim Abschicken: bis der Chunk dran ist, kann der Nutzer schon
    // abgebrochen haben, und cancelJob() muss das als "fileId noch unbekannt" behandeln.
    for (const job of jobs) {
      this.presignPendingClientIds.add(job.clientId);
    }

    this.presignQueue.push({jobs, projectId, parentId});
    this.dispatchPresignQueue();
  }

  private dispatchPresignQueue(): void {
    if (this.presignInFlight) return;

    const next = this.presignQueue.shift();
    if (!next) return;

    this.presignInFlight = true;
    this.presignChunk(next.jobs, next.projectId, next.parentId);
  }

  /** Holt die presigned PUT-URLs für einen Schwung Dateien in EINEM Backend-Request. */
  private presignChunk(jobs: UploadJob[], projectId: number, parentId?: string): void {
    // Wurde die ganze Auswahl abgebrochen, waehrend der Chunk in der Queue lag, gar nicht abschicken:
    // sonst legt das Backend Zeilen an, die der Abbruchpfad sofort wieder loescht.
    const stillWanted = jobs.some(job => {
      const current = this.jobsSubject.value.find(j => j.clientId === job.clientId);
      return current !== undefined && current.error !== 'cancelled';
    });
    if (!stillWanted) {
      for (const job of jobs) {
        this.presignPendingClientIds.delete(job.clientId);
      }
      this.presignInFlight = false;
      this.dispatchPresignQueue();
      return;
    }

    const metadata: FileMetadataDTO[] = jobs.map(job => ({
      name: job.file.name,
      size: job.file.size,
      type: job.file.type,
      // Bewusst nur der Dateiname, kein Pfad: die Ordnerstruktur legt der Aufrufer vorher an und
      // übergibt die fertige parentId. Ein Pfad mit "/" würde uploadBatch dazu bringen, dieselben
      // Ordner ein zweites Mal anzulegen.
      relativePath: job.file.name,
    }));

    this.fileController.uploadBatch(metadata, projectId, parentId ?? '').subscribe({
      next: transfers => {
        this.applyPresignResult(jobs, transfers ?? []);
        this.presignInFlight = false;
        this.dispatchPresignQueue();
      },
      error: err => {
        // Kein stiller Fallback auf den Einzel-Presign: der Upload dieser Dateien ist gescheitert und
        // muss als gescheitert sichtbar sein.
        for (const job of jobs) {
          this.presignPendingClientIds.delete(job.clientId);
          this.setJob(job.clientId, {state: 'error', error: err});
        }
        this.dispatchQueue();

        // Der naechste Chunk muss trotzdem laufen, sonst bleibt die Queue stehen.
        this.presignInFlight = false;
        this.dispatchPresignQueue();
      }
    });
  }

  /**
   * Verteilt die Antwort des Sammel-Presigns auf die Jobs. Das Backend antwortet in der Reihenfolge
   * der gesendeten Metadaten, deshalb die Zuordnung über den Index.
   */
  private applyPresignResult(jobs: UploadJob[], transfers: FileUploadStringTransfer[]): void {
    jobs.forEach((job, idx) => {
      this.presignPendingClientIds.delete(job.clientId);

      const transfer = transfers[idx];
      if (!transfer?.string || !transfer?.fileId) {
        this.setJob(job.clientId, {state: 'error', error: transfer ?? 'uploadBatch lieferte keine presigned URL'});
        return;
      }

      // setJob() räumt einen zwischenzeitlich abgebrochenen Job selbst auf, sobald die fileId bekannt
      // ist (siehe cancelJob) \- und übernimmt für ihn bewusst KEINE uploadUrl.
      this.setJob(job.clientId, {
        fileId: transfer.fileId,
        projectFile: transfer.projectFile,
        uploadUrl: transfer.string,
      });

      const current = this.jobsSubject.value.find(j => j.clientId === job.clientId);
      if (!current || current.error === 'cancelled') return;

      this.queuedClientIds.push(job.clientId);
    });

    this.dispatchQueue();
  }

  /** Bricht alle Jobs einer Dateiauswahl ab (z.B. wenn der Nutzer den Upload-Dialog verwirft). */
  public cancelBatch(batchId: string): void {
    const clientIds = this.jobsSubject.value
      .filter(j => j.batchId === batchId && (j.state === 'queued' || j.state === 'uploading' || j.state === 'processing'))
      .map(j => j.clientId);

    // Erst komplett aus der Warteschlange nehmen, damit dispatchQueue() keinen Job desselben Batches nachzieht.
    for (const clientId of clientIds) {
      const idx = this.queuedClientIds.indexOf(clientId);
      if (idx !== -1) this.queuedClientIds.splice(idx, 1);
    }

    for (const clientId of clientIds) {
      this.cancelJob(clientId);
    }
  }

  /** Jobs einer Dateiauswahl \- in Auswahlreihenfolge, damit die Liste im Dialog nicht springt. */
  public jobsForBatch$(batchId: string): Observable<UploadJob[]> {
    return this.jobs$.pipe(
      map(jobs => jobs.filter(j => j.batchId === batchId).sort((a, b) => a.createdAt - b.createdAt))
    );
  }

  /** Stoppt den laufenden Upload und räumt im Backend auf. */
  private hardCancel(clientId: string): void {
    this.cancelRequestedClientIds.delete(clientId);
    this.presignPendingClientIds.delete(clientId);

    // Noch nicht gestartete Jobs aus der Warteschlange nehmen \- sonst startet der Dispatcher sie später doch.
    const queueIdx = this.queuedClientIds.indexOf(clientId);
    if (queueIdx !== -1) {
      this.queuedClientIds.splice(queueIdx, 1);
    }

    const sub = this.activeSubscriptions.get(clientId);
    sub?.unsubscribe();
    this.activeSubscriptions.delete(clientId);

    const cancelled = this.jobsSubject.value.find(j => j.clientId === clientId);
    if (cancelled) {
      this.cleanupCancelledJob(cancelled);
    }

    // Freigewordenes Budget für andere Batches nutzen (der complete-Handler läuft nach unsubscribe nicht mehr).
    this.releaseSlot(clientId);
    this.dispatchQueue();
  }

  private markCancelled(clientId: string): void {
    const jobs = this.jobsSubject.value;
    const idx = jobs.findIndex(j => j.clientId === clientId);
    if (idx === -1 || jobs[idx].error === 'cancelled') return;

    const updated: UploadJob = {...jobs[idx], state: 'error', error: 'cancelled'};
    const next = [...jobs];
    next[idx] = updated;
    this.jobsSubject.next(next);
    this.jobUpdatesSubject.next(updated);
  }

  /**
   * Räumt einen abgebrochenen Job im Backend auf: erst den Multipart-Upload in S3 abbrechen
   * (sonst bleiben bezahlte Parts liegen), danach den bereits angelegten Datei-Record löschen.
   * Bewusst nacheinander \- beide Endpoints schreiben auf dieselbe Zeile und laufen parallel in einen Konflikt.
   */
  private cleanupCancelledJob(job: UploadJob): void {
    const fileId = job.fileId;
    if (!fileId) return;

    const abort$ = job.uploadId
      ? this.fileController.abortMultipartUpload({uploadId: job.uploadId}, job.projectId, fileId)
        .pipe(catchError(() => of(null)))
      : of(null);

    abort$.pipe(
      mergeMap(() => this.fileController.deleteProjectFile(job.projectId, fileId))
    ).subscribe({
      error: () => { /* Best-Effort, Fehler ignorieren */
      }
    });
  }

  public clearFinished(projectId?: number): void {
    const filtered = this.jobsSubject.value.filter(j => {
      if (projectId != null && j.projectId !== projectId) return true;
      return !(j.state === 'done' || j.state === 'error');
    });
    this.jobsSubject.next(filtered);
  }

  private setJob(clientId: string, patch: Partial<UploadJob>): void {
    const jobs = this.jobsSubject.value;
    const idx = jobs.findIndex(j => j.clientId === clientId);
    if (idx === -1) return;

    // Ein bereits abgebrochener Job bleibt abgebrochen \- Fortschritt/Status aus der noch laufenden
    // Pipeline dürfen ihn nicht wiederbeleben.
    const wasCancelled = jobs[idx].error === 'cancelled';
    const updated: UploadJob = wasCancelled
      ? {...jobs[idx], fileId: patch.fileId ?? jobs[idx].fileId, uploadId: patch.uploadId ?? jobs[idx].uploadId}
      : {...jobs[idx], ...patch};

    const next = [...jobs];
    next[idx] = updated;
    this.jobsSubject.next(next);
    this.jobUpdatesSubject.next(updated);

    // Abbruch war schneller als die upload/initiate-Antwort: jetzt ist die fileId bekannt, also aufräumen.
    if (updated.fileId && this.cancelRequestedClientIds.has(clientId)) {
      this.hardCancel(clientId);
    }
  }

  /** Wie viel Verbindungsbudget ein Job belegt: Multipart lädt mehrere Parts gleichzeitig hoch. */
  private jobWeight(job: UploadJob): number {
    return job.file.size >= MULTIPART_THRESHOLD_BYTES ? MULTIPART_PART_CONCURRENCY : 1;
  }

  /** Gibt das Budget eines Jobs wieder frei (idempotent) und zieht den nächsten Job nach. */
  private releaseSlot(clientId: string): void {
    const weight = this.slotWeightByClientId.get(clientId);
    if (weight === undefined) return;

    this.slotWeightByClientId.delete(clientId);
    this.activeWeight = Math.max(0, this.activeWeight - weight);
    this.dispatchQueue();
  }

  private dispatchQueue(): void {
    while (this.queuedClientIds.length > 0) {
      const nextId = this.queuedClientIds[0];
      const job = this.jobsSubject.value.find(j => j.clientId === nextId);
      if (!job) {
        this.queuedClientIds.shift();
        continue;
      }

      // Läuft gerade nichts, startet der nächste Job immer \- auch wenn er allein das Budget sprengt.
      const weight = this.jobWeight(job);
      if (this.activeWeight > 0 && this.activeWeight + weight > MAX_PARALLEL_REQUESTS) return;

      this.queuedClientIds.shift();
      this.startJob(nextId);
    }
  }

  private startJob(clientId: string): void {
    // avoid double start
    if (this.activeSubscriptions.has(clientId)) return;

    const job = this.jobsSubject.value.find(j => j.clientId === clientId);
    if (!job) return;

    const weight = this.jobWeight(job);
    this.slotWeightByClientId.set(clientId, weight);
    this.activeWeight += weight;

    this.setJob(clientId, {state: 'uploading', progress: 0});

    // 1) + 2) Bytes hochladen: kleine Dateien per Single-PUT, große per S3 Multipart Upload.
    //    Beide Pfade setzen job.fileId und liefern true, sobald die Bytes in S3 liegen.
    const upload$ = job.file.size >= MULTIPART_THRESHOLD_BYTES
      ? this.runMultipartUpload(clientId, job)
      : this.runSingleUpload(clientId, job);

    const sub = upload$
      .pipe(
        mergeMap(ok => {
          // Die Bytes sind durch \- das Budget gehört ab hier der nächsten Datei. Der classify-Call
          // ist nur ein kurzer Backend-Roundtrip (die Klassifizierung selbst läuft dort @Async).
          this.releaseSlot(clientId);

          if (!ok) return EMPTY;

          // 3) Der Upload dieser Datei ist hier FERTIG. Die Klassifizierung wird bewusst NICHT
          //    mehr hier angestossen: das war ein zusaetzlicher Request je Datei, mitten in die
          //    noch laufenden Uploads hinein (bei ~380 Dateien also ~380), was den Netzwerk-Tab
          //    volllaufen liess und die Oberflaeche ruckeln. Sie laeuft jetzt als EIN Stapel,
          //    sobald der ganze Upload durch ist - siehe dispatchClassificationIfBatchFinished.
          const current = this.jobsSubject.value.find(j => j.clientId === clientId);
          const fileId = current?.fileId;
          if (!fileId) {
            this.setJob(clientId, {state: 'error', error: 'missing fileId after upload'});
            return EMPTY;
          }
          return of(true);
        }),
        tap(success => {
          if (success) {
            this.setJob(clientId, {state: 'done', progress: 100});
          }
        }),
        catchError(err => {
          this.setJob(clientId, {state: 'error', error: err});
          return of(false);
        })
      )
      .subscribe({
        complete: () => {
          this.activeSubscriptions.delete(clientId);
          this.cancelRequestedClientIds.delete(clientId);
          // Fehlerpfade kommen hier an, ohne dass releaseSlot() schon lief.
          this.releaseSlot(clientId);
          this.dispatchQueue();

                // Dem Server melden, dass die Bytes durch sind - unabhaengig von der Klassifizierung und
          // deutlich zeitnaher als deren Bloecke.
          this.reportUploaded(clientId, job.projectId);

    // Fertige Datei einsammeln und - je nach Blockgroesse bzw. Ende des Stapels - die
          // Klassifizierung anstossen. Ein Request je Block statt einem je Datei.
          this.collectForClassification(clientId);
          this.dispatchClassification(job.batchId, job.projectId);

          // Cleanup: sobald nichts Aktives mehr läuft, fertige Jobs entfernen,
          // damit ein neuer Batch wieder bei 0% startet.
          if (!this.hasPendingUploadWork()) {
            this.clearFinished();
          }
        }
      });

    this.activeSubscriptions.set(clientId, sub);
  }

  /**
   * Klassischer Single-PUT über eine presigned URL (Dateien < 5 GiB).
   *
   * Die URL kommt aus dem gebündelten Presign (siehe presignChunk) und liegt beim Start schon am Job \-
   * hier fällt deshalb kein eigener Backend-Roundtrip pro Datei mehr an.
   */
  private runSingleUpload(clientId: string, job: UploadJob): Observable<boolean> {
    if (!job.uploadUrl || !job.fileId) {
      this.setJob(clientId, {state: 'error', error: 'Job ohne presigned URL gestartet'});
      return EMPTY;
    }

    // Letzter gemeldeter Ganzzahl-Prozentwert. Jedes setJob() kopiert das komplette Job-Array und löst
    // über jobs$ eine Change-Detection-Runde aus; der Browser feuert UploadProgress aber weit
    // häufiger als sich ein Prozentpunkt ändert. Bei vielen Dateien war das der Hauptteil der
    // Renderlast, ohne dass sich an der Anzeige etwas geändert hätte.
    let lastReportedPct = -1;

    return this.putFileWithProgress(job.uploadUrl, job.file).pipe(
      tap(evt => {
        if (evt.type === HttpEventType.UploadProgress) {
          const total = evt.total ?? job.file.size;
          const pct = total > 0 ? Math.round((100 * evt.loaded) / total) : 0;
          if (pct !== lastReportedPct) {
            lastReportedPct = pct;
            this.setJob(clientId, {progress: pct, state: 'uploading'});
          }
        }
      }),
      filter(evt => evt instanceof HttpResponse),
      map(() => true)
    );
  }

  /**
   * S3 Multipart Upload für große Dateien:
   * initiate (Backend) -> Parts parallel per presigned PUT hochladen -> complete (Backend).
   * Bei Fehler wird der Upload in S3 abgebrochen.
   */
  private runMultipartUpload(clientId: string, job: UploadJob): Observable<boolean> {
    // initiate über den generierten Client (Backend liefert uploadId + presigned URL je Part).
    return this.fileController.initiateMultipartUpload(job.projectId, job.file.name, job.file.size, job.parentId ?? '').pipe(
      mergeMap(init => {
        if (!init?.fileId || !init?.uploadId || !init?.parts?.length) {
          this.setJob(clientId, {state: 'error', error: init});
          return EMPTY;
        }

        this.setJob(clientId, {fileId: init.fileId, projectFile: init.projectFile, uploadId: init.uploadId});

        const fileId = init.fileId;
        const uploadId = init.uploadId;
        const partSize = init.partSize ?? 0;

        // Progress über alle Parts hinweg aggregieren (byte-genau).
        const loadedByPart = new Map<number, number>();
        const emitProgress = () => {
          let loaded = 0;
          loadedByPart.forEach(v => loaded += v);
          const pct = job.file.size > 0 ? Math.round((100 * loaded) / job.file.size) : 0;
          // 100% erst nach erfolgreichem complete signalisieren.
          this.setJob(clientId, {progress: Math.min(99, pct), state: 'uploading'});
        };

        return from(init.parts).pipe(
          mergeMap(part => this.uploadPart(job.file, part, partSize, loadedByPart, emitProgress), MULTIPART_PART_CONCURRENCY),
          toArray(),
          mergeMap(() => this.completeMultipart(job, fileId, uploadId))
        );
      }),
      catchError(err => {
        const current = this.jobsSubject.value.find(j => j.clientId === clientId);
        if (current) {
          this.abortMultipart(current);
        }
        this.setJob(clientId, {state: 'error', error: err});
        return of(false);
      })
    );
  }

  /**
   * Lädt einen einzelnen Part (Blob-Slice) per presigned PUT direkt zu S3 hoch.
   * Der ETag wird bewusst NICHT im Browser gelesen \- das Backend ermittelt ihn beim complete via ListParts
   * (unabhängig von der S3-CORS-Einstellung "ExposeHeaders: ETag").
   */
  private uploadPart(file: File, part: MultipartPartUrl, partSize: number, loadedByPart: Map<number, number>, emitProgress: () => void): Observable<void> {
    const partNumber = part.partNumber ?? 0;
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);

    // Direkter PUT an die presigned S3-URL \- KEIN Backend-Endpoint, daher bewusst kein generierter Client.
    const req = new HttpRequest('PUT', part.url ?? '', blob, {reportProgress: true});
    return this.http.request(req).pipe(
      tap(evt => {
        if (evt.type === HttpEventType.UploadProgress) {
          loadedByPart.set(partNumber, evt.loaded);
          emitProgress();
        }
      }),
      filter(evt => evt instanceof HttpResponse),
      map(() => {
        loadedByPart.set(partNumber, end - start);
        emitProgress();
      }),
      // Einzelne Parts bei transienten Netzwerkfehlern erneut versuchen.
      retry(2)
    );
  }

  /** Schließt den Multipart Upload über den generierten Client ab (Backend holt Part-ETags via ListParts). */
  private completeMultipart(job: UploadJob, fileId: string, uploadId: string): Observable<boolean> {
    return this.fileController.completeMultipartUpload({uploadId}, job.projectId, fileId).pipe(map(() => true));
  }

  /** Bricht einen laufenden Multipart Upload in S3 ab (Best-Effort) über den generierten Client. */
  private abortMultipart(job: UploadJob): void {
    if (!job.fileId || !job.uploadId) return;
    this.fileController.abortMultipartUpload({uploadId: job.uploadId}, job.projectId, job.fileId)
      .subscribe({
        error: () => { /* Best-Effort, Fehler ignorieren */
        }
      });
  }

  /**
   * Merkt eine fertig hochgeladene Datei fuer die Klassifizierung vor.
   *
   * Der Zustand wird hier frisch gelesen: die im Abschluss-Handler geschlossene `job`-Referenz ist
   * der Stand vom Start des Uploads, `setJob` erzeugt jedes Mal ein neues Objekt.
   *
   * Nur erfolgreich hochgeladene Dateien mit fileId gehen mit; abgebrochene und fehlgeschlagene
   * haben serverseitig nichts zu klassifizieren.
   */
  private collectForClassification(clientId: string): void {
    if (this.classificationCollectedClientIds.has(clientId)) return;

    const job = this.jobsSubject.value.find(j => j.clientId === clientId);
    if (!job || job.state !== 'done' || !job.fileId) return;

    this.classificationCollectedClientIds.add(clientId);
    const pending = this.classificationPendingByBatchId.get(job.batchId) ?? [];
    pending.push({fileId: job.fileId, name: job.file.name});
    this.classificationPendingByBatchId.set(job.batchId, pending);
  }

  /**
   * Reicht vorgemerkte Dateien zur Klassifizierung ein - blockweise waehrend des Uploads, und am
   * Ende den Rest.
   *
   * Warum nicht ein Request je Datei (wie fruher) und nicht nur einer am Schluss:
   * - Je Datei waren es ~380 zusaetzliche Requests mitten in die laufenden Uploads hinein; der
   *   Netzwerk-Tab lief voll und die Oberflaeche ruckelte.
   * - Nur am Schluss blieb jede Datei bis zum Ende des ganzen Uploads auf Status UPLOADING stehen.
   * Ein Block je 50 Dateien loest beides: ~8 Requests statt 380, und der Status stimmt kurz nach
   * jedem Block.
   */
  private dispatchClassification(batchId: string, projectId: number): void {
    if (!batchId) return;

    const pending = this.classificationPendingByBatchId.get(batchId);
    if (!pending || pending.length === 0) return;

    const finished = this.isUploadBatchFinished(batchId);
    while (pending.length >= CLASSIFICATION_CHUNK_SIZE || (finished && pending.length > 0)) {
      const chunk = pending.splice(0, CLASSIFICATION_CHUNK_SIZE);
      this.classificationService.classifyUploadedFiles(projectId, chunk);
    }

    if (finished) {
      this.classificationPendingByBatchId.delete(batchId);
      // Merker des Stapels aufraeumen, sonst waechst das Set ueber die Sitzung hinweg mit jeder je
      // hochgeladenen Datei mit.
      for (const job of this.jobsSubject.value.filter(job => job.batchId === batchId)) {
        this.classificationCollectedClientIds.delete(job.clientId);
      }
    }
  }

  /**
   * Merkt eine fertige Datei zur Meldung vor und schickt die Sammlung ab, wenn nichts unterwegs ist.
   *
   * Der Zustand wird frisch gelesen: die im Abschluss-Handler geschlossene `job`-Referenz ist der
   * Stand vom Start des Uploads (siehe collectForClassification).
   */
  private reportUploaded(clientId: string, projectId: number): void {
    const job = this.jobsSubject.value.find(j => j.clientId === clientId);
    if (!job || job.state !== 'done' || !job.fileId) return;

    this.uploadedToReport.push(job.fileId);
    this.flushUploadedReport(projectId);
  }

  private flushUploadedReport(projectId: number): void {
    if (this.uploadedReportInFlight || this.uploadedToReport.length === 0) return;

    const batch = this.uploadedToReport.splice(0);
    this.uploadedReportInFlight = true;
    this.fileController.markFilesUploaded(batch, projectId).subscribe({
      next: () => {
        this.uploadedReportInFlight = false;
        this.uploadedReportedSubject.next(batch);
        // Was sich waehrenddessen angesammelt hat, geht jetzt raus.
        this.flushUploadedReport(projectId);
      },
      error: () => {
        // Best-Effort: die Meldung ist nur eine Statusverfeinerung. Bleibt sie aus, steht die Datei
        // weiter auf UPLOADING und der UploadTimeoutSweeper faengt sie spaeter ein. Nicht erneut
        // versuchen - ein Fehlschlag hier darf den Upload nicht ausbremsen.
        this.uploadedReportInFlight = false;
        this.flushUploadedReport(projectId);
      },
    });
  }

  /** Ob von diesem Upload-Stapel nichts mehr laeuft und auch nichts mehr nachkommt. */
  private isUploadBatchFinished(batchId: string): boolean {
    const batchJobs = this.jobsSubject.value.filter(job => job.batchId === batchId);
    if (batchJobs.length === 0) return false;

    const stillRunning = batchJobs.some(job =>
      job.state === 'queued' || job.state === 'uploading' || job.state === 'processing');
    // Die Presign-Warteschlange muss mit: bei einem Ordner-Upload werden die URLs in Chunks geholt,
    // es koennen also noch Dateien nachkommen, obwohl gerade gar keine laeuft - zwischen zwei Chunks
    // ist weder eine Subscription aktiv noch ein Job in queued.
    return !stillRunning
      && this.presignPendingClientIds.size === 0
      && this.presignQueue.length === 0
      && !this.presignInFlight;
  }

  private putFileWithProgress(url: string, file: File): Observable<HttpEvent<unknown>> {
    const req = new HttpRequest('PUT', url, file, {reportProgress: true});
    return this.http.request(req);
  }

  /**
   * Läuft oder wartet noch Upload-Arbeit?
   *
   * Die Presign-Queue MUSS mitzählen: zwischen zwei Chunks ist weder eine Subscription aktiv noch
   * etwas in queuedClientIds, die Dateien der noch nicht presignten Chunks kommen aber sicher noch.
   * Ohne diese Bedingung raeumte clearFinished() mitten im Batch die bereits fertigen Jobs weg \-
   * der Fortschrittsdialog rechnete danach ueber weniger Jobs und zeigte eine Prozentzahl, die nicht
   * mehr zur angezeigten Dateizahl passte.
   */
  private hasPendingUploadWork(): boolean {
    return this.activeSubscriptions.size > 0
      || this.queuedClientIds.length > 0
      || this.presignInFlight
      || this.presignQueue.length > 0
      || this.presignPendingClientIds.size > 0;
  }

  /** True, sobald mindestens ein Upload-Job in queued/uploading/processing ist. */
  public readonly hasActiveUploads$: Observable<boolean> = this.jobs$.pipe(
    map(jobs => jobs.some(j => j.state === 'queued' || j.state === 'uploading' || j.state === 'processing'))
  );

  /**
   * Meldet dem Server die Dateien, deren Bytes nie ankommen werden.
   *
   * Aufgerufen beim Entladen der Seite (siehe PendingWorkUnloadGuardService). Bis dahin blieb eine
   * solche Zeile serverseitig auf UPLOADING stehen, bis der UploadTimeoutSweeper sie nach einer
   * Stunde einsammelte - und die Dateiansicht zeigte sie in dieser Zeit als "Analysieren", weil
   * UPLOADING ohne laufenden Transfer dort als "Bytes durch, Verarbeitung wartet" gedeutet wird.
   * Nicht loeschbar, nicht erklaert, und von einer echten Analyse nicht zu unterscheiden.
   *
   * Die Frist des Sweepers laesst sich nicht einfach verkuerzen: er darf einen NOCH LAUFENDEN
   * Upload unter keinen Umstaenden abraeumen und kann nicht wissen, ob die Gegenstelle noch lebt.
   * Dieser Tab weiss es - er ist die Gegenstelle.
   *
   * Gemeldet werden NUR Jobs, die noch laufen oder warten. Ein Job in `done` ist ausgenommen, auch
   * wenn seine UPLOADED-Meldung noch in der Warteschlange steht: dessen Bytes liegen in S3, und ihn
   * als gescheitert zu melden waere schlimmer als die Verzoegerung, die er ohnehin nur hat.
   */
  public reportAbortedUploads(): void {
    const byProject = new Map<number, string[]>();
    for (const job of this.jobsSubject.value) {
      if (!job.fileId) continue;
      if (job.state !== 'queued' && job.state !== 'uploading' && job.state !== 'processing') continue;
      if (job.error === 'cancelled') continue;

      const ids = byProject.get(job.projectId);
      if (ids) ids.push(job.fileId);
      else byProject.set(job.projectId, [job.fileId]);
    }

    for (const [projectId, fileIds] of byProject) {
      this.postAbortedBeacon(projectId, fileIds);
    }

    this.flushPendingClassificationOnUnload();
  }

  /**
   * Schickt die schon hochgeladenen, aber noch nicht eingereichten Dateien zur Klassifizierung raus.
   *
   * Die zweite Haelfte des Reload-Problems, und die groessere: die Klassifizierung geht in Bloecken
   * zu {@link CLASSIFICATION_CHUNK_SIZE} raus, es warten also je Stapel bis zu 49 fertige Dateien
   * darauf, dass der Block voll wird. Deren Bytes sind durch - serverseitig stehen sie auf
   * UPLOADED, nicht auf UPLOADING, und {@link reportAbortedUploads} fasst sie zu Recht nicht an.
   * Eingereicht hat sie aber auch niemand, und diese Liste lebt nur im Tab.
   *
   * Aufgesammelt wurden sie bisher erst vom UploadTimeoutSweeper - nach einer Stunde. Weil UPLOADED
   * in der Dateiansicht als "wird analysiert" erscheint, sah das aus wie eine Klassifizierung, die
   * ewig laedt. Genau der Eindruck, den der Test am 2026-09-14 ergeben hat.
   *
   * Hier gehen sie in EINEM Aufruf je Stapel raus, ohne auf einen vollen Block zu warten: es ist
   * die letzte Gelegenheit. Die Blockgroesse ist eine Optimierung fuer den laufenden Betrieb, keine
   * Obergrenze - der Endpunkt nimmt die ganze Liste.
   */
  private flushPendingClassificationOnUnload(): void {
    for (const [batchId, pending] of this.classificationPendingByBatchId) {
      if (pending.length === 0) continue;

      // Das Projekt steht nicht an der Sammlung, sondern an den Jobs des Stapels.
      const job = this.jobsSubject.value.find(j => j.batchId === batchId);
      if (!job) continue;

      this.postClassificationBeacon(job.projectId, pending.map(entry => entry.fileId));
    }
    this.classificationPendingByBatchId.clear();
  }

  /** Wie {@link postAbortedBeacon}, nur auf den Klassifizierungs-Endpunkt. */
  private postClassificationBeacon(projectId: number, fileIds: string[]): void {
    if (fileIds.length === 0) return;

    const basePath = (this.fileController as any).basePath ?? '';
    // Synchron aus dem localStorage, nicht ueber TokenRefreshService.getValidToken(): beim Entladen
    // laesst sich auf keinen Refresh warten. Ist das Token abgelaufen, scheitert die Meldung mit 401
    // und der UploadTimeoutSweeper raeumt auf (siehe postAbortedBeacon).
    const token = localStorage.getItem('token') ?? '';
    const url = `${basePath}/projects/${projectId}/files/classification-batches?projectId=${projectId}`;

    try {
      void fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fileIds),
      }).catch(() => undefined);
    } catch {
      // Best-Effort wie beim Abbruch-Melden: schlaegt es fehl, greift serverseitig der
      // UploadTimeoutSweeper.
    }
  }

  /**
   * Der Meldeweg beim Entladen: `fetch` mit `keepalive`, nicht der HttpClient.
   *
   * Ein normaler Request wird beim Entladen der Seite abgebrochen - genau in dem Moment, in dem
   * diese Meldung rausgehen muss. `keepalive` laesst den Browser ihn zu Ende bringen, auch wenn das
   * Dokument schon weg ist. `navigator.sendBeacon` kaeme ebenfalls infrage, kann aber keine Header
   * setzen, und die API authentifiziert ueber ein Bearer-Token.
   *
   * Best-Effort: schlaegt es fehl, bleibt alles beim Alten und der UploadTimeoutSweeper raeumt
   * spaeter auf. Deshalb wird der Fehler bewusst verschluckt - waehrend des Entladens gibt es
   * niemanden mehr, dem man ihn zeigen koennte.
   */
  private postAbortedBeacon(projectId: number, fileIds: string[]): void {
    const basePath = (this.fileController as any).basePath ?? '';
    // Synchron, ohne Refresh: beim Entladen kann nichts mehr auf ein Promise warten. Ein abgelaufenes
    // Token laesst die Meldung mit 401 scheitern - Best-Effort wie oben beschrieben.
    const token = localStorage.getItem('token') ?? '';
    const url = `${basePath}/projects/${projectId}/files/uploads/aborted?projectId=${projectId}`;

    try {
      void fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fileIds),
      }).catch(() => undefined);
    } catch {
      // s.o. - Best-Effort
    }
  }

  /**
   * Fortschritt fuer die Hintergrund-Badge in der App-Shell - EIN Eintrag JE PROJEKT.
   *
   * Notwendig, weil ein laufender Upload ausserhalb der Dateiansicht bisher voellig unsichtbar war:
   * bic-file-upload-progress haengt nur dort, und der Fortschritts-Dialog ist ein Popup - wer
   * wegnavigierte, sah nichts mehr. Gleiche Form wie DeleteService, damit die Badge alle Vorgaenge
   * ohne Sonderfaelle anzeigen kann.
   *
   * Warum je Projekt und nicht als eine Summe: die Warteschlange ist tabweit, ein Upload laeuft
   * beim Projektwechsel weiter, und danach kann ein zweiter in einem anderen Projekt starten. Eine
   * gemeinsame Zeile haette beide Zaehler vermischt und - schlimmer - die Badge haette beim Klick
   * nur EIN Projekt anspringen koennen, ohne sagen zu koennen welches.
   */
  public readonly progressByProject$: Observable<BackgroundProgress[]> = this.jobs$.pipe(
    map(jobs => {
      const byProject = new Map<number, UploadJob[]>();
      for (const job of jobs) {
        const bucket = byProject.get(job.projectId);
        if (bucket) bucket.push(job);
        else byProject.set(job.projectId, [job]);
      }

      return Array.from(byProject.entries()).map(([projectId, own]) => ({
        total: own.length,
        done: own.filter(job => job.state === 'done').length,
        failed: own.filter(job => job.state === 'error').length,
        active: own.filter(job => job.state === 'queued' || job.state === 'uploading' || job.state === 'processing').length,
        currentNames: own
          .filter(job => job.state === 'uploading' || job.state === 'processing')
          .map(job => job.file.name),
        failedNames: own.filter(job => job.state === 'error').map(job => job.file.name),
        projectId,
      }));
    }),
  );

  /** Tabweite Zusammenfassung - fuer alles, was nicht je Projekt gedacht ist. */
  public readonly progress$: Observable<BackgroundProgress> = this.progressByProject$.pipe(
    map(mergeProgress),
  );

}

